import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'

const formatConnectedAt = (createdAt, locale) => {
  if (!createdAt) return null
  const date = new Date(createdAt)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' })
}

const formatCompactTimestamp = (timestamp, locale) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const EVENTS_PER_PAGE = 3
const ALL_EVENTS_BATCH_SIZE = 200

const AutomationsCard = ({ businessId }) => {
  const { t, i18n } = useTranslation('dashboard')
  const locale = i18n.language === 'es' ? 'es-ES' : 'en-US'
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [integrations, setIntegrations] = useState([])
  const [pagedEventsByIntegration, setPagedEventsByIntegration] = useState({})
  const [allEventsByIntegration, setAllEventsByIntegration] = useState({})
  const [eventCountByIntegration, setEventCountByIntegration] = useState({})
  const [currentPageByIntegration, setCurrentPageByIntegration] = useState({})
  const [viewModeByIntegration, setViewModeByIntegration] = useState({})
  const [loadingPageByIntegration, setLoadingPageByIntegration] = useState({})
  const [loadingAllByIntegration, setLoadingAllByIntegration] = useState({})
  const currentPageRef = useRef({})
  const viewModeRef = useRef({})

  const visibleEventsByIntegration = useMemo(() => {
    const map = {}
    for (const integration of integrations) {
      if (viewModeByIntegration[integration.id] === 'expanded') {
        map[integration.id] = allEventsByIntegration[integration.id] || []
      } else {
        map[integration.id] = pagedEventsByIntegration[integration.id] || []
      }
    }
    return map
  }, [allEventsByIntegration, integrations, pagedEventsByIntegration, viewModeByIntegration])

  const fetchEventPage = async (integrationId, page) => {
    if (!businessId || !integrationId || !page) return []
    const from = (page - 1) * EVENTS_PER_PAGE
    const to = from + EVENTS_PER_PAGE - 1
    const { data, error: eventsError } = await supabase
      .from('automation_events')
      .select(
        'id, business_id, integration_id, event_key, event_date, source, rendered_title, rendered_body, received_at'
      )
      .eq('business_id', businessId)
      .eq('integration_id', integrationId)
      .order('received_at', { ascending: false })
      .range(from, to)

    if (eventsError) throw eventsError
    return data || []
  }

  const fetchAllEventsForIntegration = async (integrationId) => {
    if (!businessId || !integrationId) return []
    let from = 0
    let hasMore = true
    const allEvents = []

    while (hasMore) {
      const to = from + ALL_EVENTS_BATCH_SIZE - 1
      const { data, error: eventsError } = await supabase
        .from('automation_events')
        .select(
          'id, business_id, integration_id, event_key, event_date, source, rendered_title, rendered_body, received_at'
        )
        .eq('business_id', businessId)
        .eq('integration_id', integrationId)
        .order('received_at', { ascending: false })
        .range(from, to)

      if (eventsError) throw eventsError

      const chunk = data || []
      allEvents.push(...chunk)
      hasMore = chunk.length === ALL_EVENTS_BATCH_SIZE
      from += ALL_EVENTS_BATCH_SIZE
    }

    return allEvents
  }

  const fetchEventCount = async (integrationId) => {
    if (!businessId || !integrationId) return 0
    const { count, error: countError } = await supabase
      .from('automation_events')
      .select('id', { count: 'exact', head: true })
      .eq('business_id', businessId)
      .eq('integration_id', integrationId)

    if (countError) throw countError
    return count || 0
  }

  const fetchAutomationData = async () => {
    if (!businessId) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const { data: integrationsData, error: integrationsError } = await supabase
        .from('automation_integrations')
        .select('id, name, integration_type, is_active, created_at')
        .eq('business_id', businessId)
        .order('created_at', { ascending: true })

      if (integrationsError) throw integrationsError

      setIntegrations(integrationsData || [])

      const nextPagedEvents = {}
      const nextEventCounts = {}
      const nextCurrentPages = {}
      const nextViewModes = {}

      await Promise.all(
        (integrationsData || []).map(async (integration) => {
          const [totalCount, firstPageEvents] = await Promise.all([
            fetchEventCount(integration.id),
            fetchEventPage(integration.id, 1),
          ])
          nextPagedEvents[integration.id] = firstPageEvents
          nextEventCounts[integration.id] = totalCount
          nextCurrentPages[integration.id] = 1
          nextViewModes[integration.id] = 'paged'
        })
      )

      setPagedEventsByIntegration(nextPagedEvents)
      setEventCountByIntegration(nextEventCounts)
      setCurrentPageByIntegration(nextCurrentPages)
      setViewModeByIntegration(nextViewModes)
      setAllEventsByIntegration({})
      setLoadingPageByIntegration({})
      setLoadingAllByIntegration({})
    } catch (err) {
      console.error('Failed to fetch automation data:', err)
      setError(t('automation.loadError'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAutomationData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId])

  useEffect(() => {
    currentPageRef.current = currentPageByIntegration
  }, [currentPageByIntegration])

  useEffect(() => {
    viewModeRef.current = viewModeByIntegration
  }, [viewModeByIntegration])

  useEffect(() => {
    if (!businessId) return

    const channel = supabase
      .channel(`automation-events-${businessId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'automation_events',
          filter: `business_id=eq.${businessId}`,
        },
        (payload) => {
          if (!payload.new) return
          const inserted = payload.new

          setEventCountByIntegration((prev) => ({
            ...prev,
            [inserted.integration_id]: (prev[inserted.integration_id] || 0) + 1,
          }))

          setAllEventsByIntegration((prev) => {
            const current = prev[inserted.integration_id]
            if (!current) return prev
            if (current.some((ev) => ev.id === inserted.id)) return prev
            return {
              ...prev,
              [inserted.integration_id]: [inserted, ...current],
            }
          })

          setPagedEventsByIntegration((prev) => {
            const current = prev[inserted.integration_id]
            if (!current) return prev
            const onFirstPage = (currentPageRef.current[inserted.integration_id] || 1) === 1
            const inPagedMode = (viewModeRef.current[inserted.integration_id] || 'paged') === 'paged'
            if (!onFirstPage || !inPagedMode) return prev
            if (current.some((ev) => ev.id === inserted.id)) return prev
            return {
              ...prev,
              [inserted.integration_id]: [inserted, ...current].slice(0, EVENTS_PER_PAGE),
            }
          })
        }
      )

    channel.subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [businessId])

  const handleCyclePage = async (integrationId) => {
    const totalEvents = eventCountByIntegration[integrationId] || 0
    const totalPages = Math.max(1, Math.ceil(totalEvents / EVENTS_PER_PAGE))
    if (totalPages <= 1) return

    const currentPage = currentPageByIntegration[integrationId] || 1
    const nextPage = currentPage >= totalPages ? 1 : currentPage + 1

    setLoadingPageByIntegration((prev) => ({ ...prev, [integrationId]: true }))
    try {
      const pageEvents = await fetchEventPage(integrationId, nextPage)
      setPagedEventsByIntegration((prev) => ({ ...prev, [integrationId]: pageEvents }))
      setCurrentPageByIntegration((prev) => ({ ...prev, [integrationId]: nextPage }))
    } catch (err) {
      console.error('Failed to fetch automation page:', err)
    } finally {
      setLoadingPageByIntegration((prev) => ({ ...prev, [integrationId]: false }))
    }
  }

  const handleViewMore = async (integrationId) => {
    setLoadingAllByIntegration((prev) => ({ ...prev, [integrationId]: true }))
    try {
      const allEvents = await fetchAllEventsForIntegration(integrationId)
      setAllEventsByIntegration((prev) => ({ ...prev, [integrationId]: allEvents }))
      setViewModeByIntegration((prev) => ({ ...prev, [integrationId]: 'expanded' }))
    } catch (err) {
      console.error('Failed to fetch full automation history:', err)
    } finally {
      setLoadingAllByIntegration((prev) => ({ ...prev, [integrationId]: false }))
    }
  }

  const handleShowLess = (integrationId) => {
    setViewModeByIntegration((prev) => ({ ...prev, [integrationId]: 'paged' }))
  }


  return (
    <section className="rounded-2xl border border-gray-200 bg-white px-5 py-5 shadow-sm sm:px-6" aria-labelledby="automation-activity-title">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 id="automation-activity-title" className="text-base font-semibold text-gray-900">
            {t('automation.activityTitle')}
          </h3>
          <p className="mt-1 text-sm text-gray-500">{t('automation.activitySubtitle')}</p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
          <span className="h-2 w-2 rounded-full bg-emerald-600" aria-hidden="true" />
          {t('automation.active')}
        </span>
      </header>

      <div className="mt-5">
        {loading ? (
          <p className="text-sm text-gray-600">{t('automation.loadingActivity')}</p>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        ) : integrations.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
            <div className="font-medium">{t('automation.noIntegrationsTitle')}</div>
            <div className="mt-1 text-gray-600">{t('automation.noIntegrationsBody')}</div>
          </div>
        ) : (
          <div className="space-y-5">
            {integrations.map((integration) => {
              const visibleEvents = visibleEventsByIntegration[integration.id] || []
              const totalEvents = eventCountByIntegration[integration.id] || 0
              const totalPages = Math.max(1, Math.ceil(totalEvents / EVENTS_PER_PAGE))
              const currentPage = currentPageByIntegration[integration.id] || 1
              const isExpanded = viewModeByIntegration[integration.id] === 'expanded'
              const isLoadingPage = !!loadingPageByIntegration[integration.id]
              const isLoadingAll = !!loadingAllByIntegration[integration.id]
              const connectedAt = formatConnectedAt(integration.created_at, locale)

              return (
                <div key={integration.id}>
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-pink-100 text-sm font-semibold text-[#FF1B54]" aria-hidden="true">
                        {(integration.name || 'A').slice(0, 1).toUpperCase()}
                      </span>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-gray-900">
                          {integration.name}
                          {!integration.is_active && (
                            <span className="ml-2 text-xs font-normal text-gray-500">({t('automation.inactive')})</span>
                          )}
                        </div>
                        <div className="mt-0.5 text-xs text-gray-500">
                          {`${totalEvents} ${totalEvents === 1 ? t('automation.eventSingular') : t('automation.eventPlural')}`}
                          {connectedAt && <span> · {t('automation.connectedAt', { date: connectedAt })}</span>}
                        </div>
                      </div>
                    </div>
                  </div>

                  {totalEvents === 0 ? (
                    <p className="py-5 text-sm text-gray-500">{t('automation.noActivityYet')}</p>
                  ) : (
                    <div className={isExpanded ? 'automation-events-scroll mt-2 max-h-96 overflow-y-auto pr-1' : 'mt-2'}>
                      {visibleEvents.map((ev) => {
                        const fullTitle = ev.rendered_title || ev.event_key
                        const integrationPrefix = `${integration.name}: `
                        const title = fullTitle.startsWith(integrationPrefix)
                          ? fullTitle.slice(integrationPrefix.length)
                          : fullTitle

                        return (
                          <div key={ev.id} className="flex items-center gap-3 border-b border-gray-100 py-3 last:border-b-0">
                            <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" aria-hidden="true" />
                            <div className="min-w-0 flex-1 truncate text-sm font-medium text-gray-800">{title}</div>
                            <time className="shrink-0 text-xs text-gray-500" dateTime={ev.received_at}>
                              {formatCompactTimestamp(ev.received_at, locale)}
                            </time>
                          </div>
                        )
                      })}
                    </div>
                  )}

                  <footer className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-3">
                    <span className="text-xs text-gray-500">{t('automation.latestActivity')}</span>
                    <div className="flex items-center gap-3">
                      {totalEvents > EVENTS_PER_PAGE && !isExpanded && (
                        <button
                          type="button"
                          disabled={isLoadingPage}
                          className="text-xs text-gray-500 hover:text-gray-800 disabled:opacity-50"
                          onClick={() => handleCyclePage(integration.id)}
                        >
                          {isLoadingPage
                            ? t('automation.loadingPage')
                            : t('automation.pageOutOf', { current: currentPage, total: totalPages })}
                        </button>
                      )}
                      {totalEvents > EVENTS_PER_PAGE && (
                        <button
                          type="button"
                          disabled={isLoadingAll}
                          className="text-base font-medium text-[#FF1B54] transition-colors hover:text-[#E1174C] disabled:opacity-50"
                          onClick={() =>
                            isExpanded ? handleShowLess(integration.id) : handleViewMore(integration.id)
                          }
                        >
                          {isLoadingAll
                            ? t('automation.loadingAll')
                            : isExpanded
                              ? t('automation.showLess')
                              : t('automation.viewMore')}
                        </button>
                      )}
                    </div>
                  </footer>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </section>
  )
}

export default AutomationsCard
